import Vue from 'vue';
import merge from '@utils/utils/merge';
import {
  getKeysMap,
  getRowIdentity,
  getColumnById,
  getColumnByKey,
  orderBy,
  findSelectedData,
  getValuesFromMap,
  toggleRowStatus,
  toggleRowStatus2
} from '../util';
import expand from './expand';
import current from './current';
import tree from './tree';

const sortData = (data, states) => {
  const sortingColumn = states.sortingColumn;
  if (!sortingColumn || typeof sortingColumn.sortable === 'string') {
    return data;
  }
  return orderBy(
    data,
    states.sortProp,
    states.sortOrder,
    sortingColumn.sortMethod,
    sortingColumn.sortBy
  );
};

const doFlattenColumns = columns => {
  const result = [];
  columns.forEach(column => {
    if (column.children) {
      result.push.apply(result, doFlattenColumns(column.children));
    } else {
      result.push(column);
    }
  });
  return result;
};
let _selectionMap = new Set();
export default Vue.extend({
  data() {
    return {
      states: {
        // 3.0 版本后要求必须设置该属性
        rowKey: null,
        selectByRowKey: false, // 勾选内部逻辑支持通过rowkey搜索

        // 渲染的数据来源，是对 table 中的 data 过滤排序后的结果
        data: [],

        // 是否包含固定列
        isComplex: false,
        // 是否显示根节点
        showRootNode: false,
        checkStrictly: true,
        noParentLink: false, // 子不关联父，当checkStrictly： false;
        // 列
        _columns: [], // 不可响应的
        columnsOderMap: [], // 列排序的映射表
        columnsGourpMap: {}, // 列分组的映射表
        originColumns: [],
        columns: [],
        fixedColumns: [],
        rightFixedColumns: [],
        leafColumns: [],
        fixedLeafColumns: [],
        rightFixedLeafColumns: [],
        leafColumnsLength: 0,
        fixedLeafColumnsLength: 0,
        rightFixedLeafColumnsLength: 0,

        // 选择
        isAllSelected: false,
        selection: [],
        reserveSelection: false,
        selectOnIndeterminate: false,
        selectable: null,

        // 单选
        currentRadio: null, // 当前单选内容
        radioable: null,

        // 过滤
        filters: {}, // 不可响应的
        filteredData: null,

        // 排序
        sortingColumn: null,
        sortProp: null,
        sortOrder: null,

        hoverRow: null,

        customColumns: {},
        // 用于对比table总数据存储
        initialColumns: [],
        // 是否显示自定义列选择面板
        showCustomColumnPanel: false,
        showCustomColumnDrawer: false,
        // 拖拽数据
        dragRawState: {
          draggingShowIndex: 0, // 拖拽的index
          openLineDrag: undefined, // 是否开启拖拽
          canDragg: false,
          dragging: false,
          dragRow: null
        }
      }
    };
  },

  computed: {
    isTree() {
      return Object.keys(this.states.treeData).length > 0;
    }
  },

  mixins: [expand, current, tree],

  methods: {
    // 检查 rowKey 是否存在
    assertRowKey() {
      const rowKey = this.states.rowKey;
      if (!rowKey) throw new Error('[ElTable] prop row-key is required');
    },

    // 更新列
    updateColumns() {
      const states = this.states;
      const _columns = states._columns || [];
      // 过滤固定列
      states.fixedColumns = _columns.filter(
        column => column.fixed === true || column.fixed === 'left'
      );
      // 过滤右侧固定列
      states.rightFixedColumns = _columns.filter(
        column => column.fixed === 'right'
      );

      if (
        states.fixedColumns.length > 0 &&
        _columns[0] &&
        _columns[0].type === 'selection' &&
        !_columns[0].fixed
      ) {
        _columns[0].fixed = true;
        states.fixedColumns.unshift(_columns[0]);
      }
      // 组合列数据
      const notFixedColumns = _columns.filter(column => !column.fixed);
      states.originColumns = []
        .concat(states.fixedColumns)
        .concat(notFixedColumns)
        .concat(states.rightFixedColumns);
      const leafColumns = doFlattenColumns(notFixedColumns);
      const fixedLeafColumns = doFlattenColumns(states.fixedColumns);
      const rightFixedLeafColumns = doFlattenColumns(states.rightFixedColumns);

      states.leafColumnsLength = leafColumns.length;
      states.fixedLeafColumnsLength = fixedLeafColumns.length;
      states.rightFixedLeafColumnsLength = rightFixedLeafColumns.length;

      states.columns = []
        .concat(fixedLeafColumns)
        .concat(leafColumns)
        .concat(rightFixedLeafColumns);
      states.isComplex =
        states.fixedColumns.length > 0 || states.rightFixedColumns.length > 0;
    },

    // 更新 DOM
    scheduleLayout(needUpdateColumns) {
      if (needUpdateColumns) {
        this.updateColumns();
      }
      this.table.debouncedUpdateLayout();
    },

    // 选择
    isSelected(row) {
      const { selection = [], selectByRowKey, rowKey} = this.states;
      if (!selectByRowKey) {
        return selection.indexOf(row) > -1;
      } else if (rowKey) {
        return this._getCurrentStatus(row, rowKey);
      } else {
        return selection.indexOf(row) > -1;
      }
    },
    _getCurrentStatus(row, rowKey) {
      const { selection = []} = this.states;
      // 如果 在paas 环境下， 当选择的数据量大于 20000 时， 切换为使用 map 存储选中数据
      if (window.amRenderEngine && selection.length >= 20000) {
        return _selectionMap.has(row[rowKey]);
      }
      return selection.findIndex((e) => {
        return e[rowKey] === row[rowKey];
      }) > -1;
    },

    clearSelection() {
      const states = this.states;
      states.isAllSelected = false;
      const oldSelection = states.selection;
      if (oldSelection.length) {
        states.selection = [];
        this.changeSelectionMap();
        this.table.$emit('selection-change', []);
      }
    },
    setSelectionList(selectionList, selected = true) {
      const { states } = this;
      const { data, selection } = states;

      // 判断是否需要更新状态
      const shouldUpdateSelection = selected && selectionList && selectionList.length;
      const shouldSelectAll = selected && (!selectionList || !selectionList.length) && states.selection.length !== data.length;
      const shouldClearSelection = !selected;

      let isChange = false;

      if (shouldUpdateSelection || shouldSelectAll || shouldClearSelection) {
        // 清空当前选择
        states.selection = [];

        if (shouldUpdateSelection) {
          // 更新选择列表
          states.selection = selectionList;
          // this._batchPush(states.selection, selectionList);
          isChange = true;
        } else if (shouldSelectAll) {
          // 全选
          states.selection.push(...data);
          // this._batchPush(states.selection, data);
          states.isAllSelected = true;
          isChange = true;
          this.table.$emit('select-all', selection);
        } else if (shouldClearSelection) {
          // 清空选择
          states.isAllSelected = false;
          isChange = true;
        }
      }

      // 触发选择变化事件
      if (isChange) {
        this.changeSelectionMap();
        this.table.$emit('selection-change', states.selection);
      }
    },
    changeSelectionMap() {
      const { selection = [], rowKey, data} = this.states;
      // 如果 在paas 环境下， 当选择的数据量大于 20000 时， 切换为使用 map 存储选中数据
      if (window.amRenderEngine && selection.length >= 20000) {
        const dataSet = new Set();
        data.forEach((item) => {
          const identity = getRowIdentity(item, rowKey);
          if (identity) {
            dataSet.add(identity);
          }
        });
        _selectionMap.clear();
        selection.forEach((item) => {
          const identity = getRowIdentity(item, rowKey);
          if (identity && dataSet.has(identity)) {
            _selectionMap.add(identity);
          }
        });
      }

    },
    clearCurrentRadio() {
      if (this.states.currentRadio) {
        this.states.currentRadio = null;
        // 调用 API 修改选中值，不触发 select 事件
        this.table.$emit('radio-change', null);
      }
    },

    cleanSelection() {
      const states = this.states;
      const { data, rowKey, selection } = states;
      let deleted;
      if (rowKey) {
        deleted = [];
        const selectedMap = getKeysMap(selection, rowKey);
        const dataMap = getKeysMap(data, rowKey);
        for (let key in selectedMap) {
          if (selectedMap.hasOwnProperty(key) && !dataMap[key]) {
            deleted.push(selectedMap[key].row);
          }
        }
      } else {
        deleted = selection.filter(item => data.indexOf(item) === -1);
      }
      if (deleted.length) {
        const newSelection = selection.filter(
          item => deleted.indexOf(item) === -1
        );
        states.selection = newSelection;
        this.changeSelectionMap();
        this.table.$emit('selection-change', newSelection.slice());
      }
    },

    toggleRowSelection(row, selected, emitChange = true) {
      const { checkStrictly, selection, lazy, rowKey, selectByRowKey } = this.states;
      let changed = false;
      if (this.isTree && !checkStrictly) {
        changed = this.setChecked(row, selected, () => {
          if (!lazy) return;
          if (emitChange) {
            this.changeSelectionMap();
            this.table.$emit(
              'selection-change',
              selection ? selection.slice() : []
            );
          }
          this.table.$emit('select-all', selection);
        });
      } else {
        if (window.amRenderEngine && selection.length >= 20000) {
          changed = toggleRowStatus2(this.states.selection, row, selected, rowKey);
        } else {
          changed = toggleRowStatus(this.states.selection, row, selected, rowKey, false, selectByRowKey);
        }
        this.changeSelectionMap();
      }
      if (changed) {
        const newSelection = (this.states.selection || []).slice();
        // 调用 API 修改选中值，不触发 select 事件
        if (emitChange) {
          this.table.$emit('select', newSelection, row);
        }
        this.table.$emit('selection-change', newSelection);
      }
    },

    toggleRowCurrentRadio(row, selected, emitChange = true) {
      if (this.states.currentRadio !== row) {
        this.states.currentRadio = row;
        // 调用 API 修改选中值，不触发 select 事件
        this.table.$emit('radio-change', row);
      }
    },

    _toggleAllSelection() {
      const states = this.states;

      const { data = [], selection, checkStrictly, lazy, rowKey, selectByRowKey } = states;
      // when only some rows are selected (but not all), select or deselect all of them
      // depending on the value of selectOnIndeterminate 8
      const value = states.selectOnIndeterminate
        ? !states.isAllSelected
        : !(states.isAllSelected || selection.length);
      states.isAllSelected = value;

      let selectionChanged = false;
      const callback = () => {
        if (!lazy) return;
        this.changeSelectionMap();
        this.table.$emit(
          'selection-change',
          selection ? selection.slice() : []
        );
        this.table.$emit('select-all', selection);
      };
      data.forEach((row, index) => {
        if (states.selectable) {
          if (
            states.selectable.call(null, row, index)
          ) {
            if (this.isTree && !checkStrictly) {
              // 处理 forEach  会将需要更新的状态 再置空的问题
              if (!selectionChanged) {
                selectionChanged = this.setChecked(row, value, callback);
              } else {
                this.setChecked(row, value, callback);
              }
            } else {
              if (!selectionChanged) {
                selectionChanged = toggleRowStatus(selection, row, value, rowKey, false, selectByRowKey);
              } else {
                toggleRowStatus(selection, row, value, rowKey, false, selectByRowKey);
              };
            }
          }
        } else {
          if (this.isTree && !checkStrictly) {
            if (!selectionChanged) {
              selectionChanged = this.setChecked(row, value, callback);
            } else {
              this.setChecked(row, value, callback);
            }
          } else {
            if (!selectionChanged) {
              selectionChanged = toggleRowStatus(selection, row, value, rowKey, false, selectByRowKey);
            } else {
              toggleRowStatus(selection, row, value, rowKey, false, selectByRowKey);
            };
          }
        }
      });

      if (selectionChanged) {
        this.changeSelectionMap();
        this.table.$emit(
          'selection-change',
          selection ? selection.slice() : []
        );
      }
      this.table.$emit('select-all', selection);
    },

    updateSelectionByRowKey() {
      const states = this.states;
      const { selection, rowKey, data, isRealTree, childrenColumnName, checkStrictly} = states;
      const selectedMap = getKeysMap(selection, rowKey);
      data.forEach(row => {
        const rowId = getRowIdentity(row, rowKey);
        const rowInfo = selectedMap[rowId];
        if (rowInfo) {
          selection[rowInfo.index] = row;
        }
      });

      // 如果需要父子联动回显示，
      if (isRealTree && !checkStrictly) {
        const childList = findSelectedData(
          data,
          selectedMap,
          childrenColumnName,
          rowKey
        );
        states.selectByRowKey = true;
        // 新旧去重
        const newSelectMap = getKeysMap(childList, rowKey);
        const newSelectList = getValuesFromMap(newSelectMap);
        states.selection = newSelectList;
        this.table.$emit(
          'selection-change',
          newSelectList ? newSelectList.slice() : []
        );
      }
      this.changeSelectionMap();

    },

    updateAllSelected() {
      const states = this.states;
      const { selection, rowKey, selectable, selectByRowKey } = states;
      // data 为 null 时，解构时的默认值会被忽略
      const data = states.data || [];
      if (data.length === 0) {
        states.isAllSelected = false;
        return;
      }

      let isSelectedFunc = null;
      if (window.amRenderEngine && selection.length >= 20000 && rowKey) {
        isSelectedFunc = function(row) {
          if (_selectionMap.size) {
            return !!_selectionMap.has(
              getRowIdentity(row, rowKey)
            );
          }
        };
      } else {
        let selectedMap;
        if (rowKey) {
          selectedMap = getKeysMap(selection, rowKey);
        }
        console.log('selectedMap', selectedMap);
        isSelectedFunc = function(row) {
          if (selectedMap) {
            return !!selectedMap[getRowIdentity(row, rowKey)];
          } else {
            if (!selectByRowKey) {
              return selection.indexOf(row) !== -1;
            } else {
              return (
                selection.findIndex((e) => {
                  return e[rowKey] === row[rowKey];
                }) !== -1
              );
            }
          }
        };
      }
      let isAllSelected = true;
      let selectedCount = 0;
      for (let i = 0, j = data.length; i < j; i++) {
        const item = data[i];
        const isRowSelectable = selectable && selectable.call(null, item, i);
        if (!isSelectedFunc(item)) {
          if (!selectable || isRowSelectable) {
            isAllSelected = false;
            break;
          }
        } else {
          selectedCount++;
        }
      }

      if (selectedCount === 0) isAllSelected = false;
      states.isAllSelected = isAllSelected;
    },

    // 过滤与排序
    updateFilters(columns, values) {
      if (!Array.isArray(columns)) {
        columns = [columns];
      }
      const states = this.states;
      const filters = {};
      columns.forEach(col => {
        states.filters[col.id] = values;
        filters[col.columnKey || col.id] = values;
      });

      return filters;
    },

    updateSort(column, prop, order) {
      if (this.states.sortingColumn && this.states.sortingColumn !== column) {
        this.states.sortingColumn.order = null;
      }
      this.states.sortingColumn = column;
      this.states.sortProp = prop;
      this.states.sortOrder = order;
    },

    execFilter() {
      const states = this.states;
      const { _data, filters } = states;
      let data = _data;

      Object.keys(filters).forEach(columnId => {
        const values = states.filters[columnId];
        if (!values || values.length === 0) return;
        const column = getColumnById(this.states, columnId);
        if (column && column.filterMethod) {
          data = data.filter(row => {
            return values.some(value =>
              column.filterMethod.call(null, value, row, column)
            );
          });
        }
      });

      states.filteredData = data;
    },

    execSort() {
      const states = this.states;
      states.data = sortData(states.filteredData, states);
    },

    // 根据 filters 与 sort 去过滤 data
    execQuery(ignore) {
      if (!(ignore && ignore.filter)) {
        this.execFilter();
      }
      this.execSort();
    },

    clearFilter(columnKeys) {
      const states = this.states;
      const {
        tableHeader,
        fixedTableHeader,
        rightFixedTableHeader
      } = this.table.$refs;

      let panels = {};
      if (tableHeader) panels = merge(panels, tableHeader.filterPanels);
      if (fixedTableHeader) {
        panels = merge(panels, fixedTableHeader.filterPanels);
      }
      if (rightFixedTableHeader) {
        panels = merge(panels, rightFixedTableHeader.filterPanels);
      }

      const keys = Object.keys(panels);
      if (!keys.length) return;

      if (typeof columnKeys === 'string') {
        columnKeys = [columnKeys];
      }

      if (Array.isArray(columnKeys)) {
        const columns = columnKeys.map(key => getColumnByKey(states, key));
        keys.forEach(key => {
          const column = columns.find(col => col.id === key);
          if (column) {
            // TODO: 优化这里的代码
            panels[key].filteredValue = [];
          }
        });
        this.commit('filterChange', {
          column: columns,
          values: [],
          silent: true,
          multi: true
        });
      } else {
        keys.forEach(key => {
          // TODO: 优化这里的代码
          panels[key].filteredValue = [];
        });

        states.filters = {};
        this.commit('filterChange', {
          column: {},
          values: [],
          silent: true
        });
      }
    },

    clearSort() {
      const states = this.states;
      if (!states.sortingColumn) return;

      this.updateSort(null, null, null);
      this.commit('changeSortCondition', {
        silent: true
      });
    },

    // 适配层，expand-row-keys 在 Expand 与 TreeTable 中都有使用
    setExpandRowKeysAdapter(val) {
      // 这里会触发额外的计算，但为了兼容性，暂时这么做
      this.setExpandRowKeys(val);
      this.updateTreeExpandKeys(val);
    },

    setShowRootNode(v) {
      this.states.showRootNode = v;
    },

    // 展开行与 TreeTable 都要使用
    toggleRowExpansionAdapter(row, expanded) {
      const hasExpandColumn = this.states.columns.some(
        ({ type }) => type === 'expand'
      );
      if (hasExpandColumn) {
        this.toggleRowExpansion(row, expanded);
      } else {
        this.toggleTreeExpansion(row, expanded);
      }
    },
    // 更新自定义列状态
    updateColumnsChecked(customColumnsLables) {
      Object.keys(this.states.customColumns).forEach(function(key) {
        this.states.customColumns[key].hide = !customColumnsLables.includes(
          this.states.customColumns[key].label
        );
        // bug: 缓存刷新后，disable = true 也会消失的问题
        if (this.states.customColumns[key].disable) {
          this.states.customColumns[key].hide = false;
        }
      }, this);
      this.table.$emit('custom-column-change', this.states.customColumns);
    },
    // 切换
    toggleCustomColumnPanelShow() {
      this.states.showCustomColumnPanel = !this.states.showCustomColumnPanel;
    },
    // 修改列排序
    changeColumnOrder(oldIndex, newIndex, column, columnNums) {
      const newMap = [];
      for (let index = 0; index < columnNums; index++) {
        const sortIndex = this.states.columnsOderMap[index] === undefined ? index : this.states.columnsOderMap[index];
        if (sortIndex === oldIndex) newMap.push(newIndex);
        else if (sortIndex <= Math.max(newIndex, oldIndex) && sortIndex >= Math.min(newIndex, oldIndex)) newMap.push(sortIndex - (newIndex > oldIndex ? 1 : -1));
        else newMap.push(sortIndex);
      }
      this.states.columnsOderMap = newMap;
    },
    changeRowOrder(oldIndex, newIndex, targetSource) {
      const data = targetSource ? targetSource : this.states.data;
      if (oldIndex === newIndex || oldIndex < 0 || oldIndex >= data.length || newIndex < 0 || newIndex >= data.length) {
        // 如果索引相同，或者索引不合法，则不进行操作
        return;
      }

      // 移除位于oldIndex的元素
      const elementToMove = data.splice(oldIndex, 1)[0];

      // 判断是向前移动还是向后移动
      if (newIndex > oldIndex) {
        data.splice(newIndex, 0, elementToMove);
      } else {
        data.splice(newIndex, 0, elementToMove);
      }
      if (targetSource) {
        targetSource = data;
      } else {
        this.states.data = data;
      }
    },
    changeTreeRowOrder(currentRow, targetRow) {
      const { data, rowKey } = this.states;
      const { _parentId } = currentRow;
      const currentRowKey = getRowIdentity(currentRow, rowKey);
      const targetRowKey = getRowIdentity(targetRow, rowKey);
      const findList = this.getFilteredTreeData(data, _parentId);
      if (findList) {
        const currentIndex = findList.findIndex(item => item[rowKey] === currentRowKey);
        const targetIndex = findList.findIndex(item => item[rowKey] === targetRowKey);
        this.changeRowOrder(currentIndex, targetIndex, findList);
        return {
          currentNode: currentRow,
          prevNode: targetIndex > 0 ? findList[targetIndex - 1] : null,
          nextNode: targetIndex < findList.length - 1 ? findList[targetIndex + 1] : null
        };
      }
      return null;
    }
  }
});
